import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Document, Page, pdfjs } from 'react-pdf';

// Set worker URL for react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const GalleryContainer = styled.div`
  margin-top: 40px;
`;

const GalleryTitle = styled.h3`
  font-size: 24px;
  color: #2c5aa0;
  margin-bottom: 20px;
  text-align: center;
`;

const SearchContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
  gap: 10px;
`;

const SearchInput = styled.input`
  padding: 12px 20px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  width: 300px;
  
  &:focus {
    outline: none;
    border-color: #2c5aa0;
  }

  @media (max-width: 768px) {
    width: 200px;
  }
`;

const SearchButton = styled.button`
  padding: 12px 24px;
  border: none;
  background: #fff;
  color: #666;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #f8f9fa;
    color: #333;
  }
`;

const BulletinGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 30px;
  margin-bottom: 30px;
`;

const BulletinCard = styled.div`
  background: #fff;
  border: 1px solid #eee;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  }
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #ddd;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
  font-size: 14px;

  ${BulletinCard}:hover & {
    opacity: 1;
  }

  &:hover {
    background: #fff;
    color: #dc3545;
    border-color: #dc3545;
  }
`;

const BulletinImageContainer = styled.div`
  width: 100%;
  padding-top: 141.42%; /* Aspect ratio for A4 paper */
  position: relative;
  background: #f8f9fa;
  overflow: hidden;
  border-bottom: 1px solid #eee;

  img, .react-pdf__Document {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  
  /* React-PDF specific styles to make it fit */
  .react-pdf__Page {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  
  .react-pdf__Page__canvas {
    width: 100% !important;
    height: auto !important;
    max-height: 100% !important;
    object-fit: contain !important;
  }
`;

const BulletinInfo = styled.div`
  padding: 15px;
`;

const BulletinDate = styled.div`
  font-size: 14px;
  color: #333;
  margin-bottom: 5px;
`;

const ChurchName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #888;
  
  img {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  z-index: 1000;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 0;
  width: 90%;
  max-width: 1000px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  line-height: 1;
  
  &:hover {
    color: #000;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  background: #f0f0f0;
  padding: 20px;
  display: flex;
  justify-content: center;
`;

const ModalFooter = styled.div`
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ActionButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  background: #2c5aa0;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.3s ease;

  &:hover {
    background: #1e3a8a;
  }
`;

interface Bulletin {
  id: string;
  title: string;
  date: string;
  thumbnailUrl?: string;
  pageUrls?: string[];
  fileUrl?: string;
  fileName?: string;
}

const mockBulletins: Bulletin[] = [
  { id: '1', title: '2025년 11월 3일 주보', date: '2025-11-03', thumbnailUrl: '/logo.png', pageUrls: ['/logo.png', '/logo.png'] },
  { id: '2', title: '2025년 10월 27일 주보', date: '2025-10-27', thumbnailUrl: '/logo.png', pageUrls: ['/logo.png', '/logo.png'] },
  { id: '3', title: '2025년 10월 20일 주보', date: '2025-10-20', thumbnailUrl: '/logo.png', pageUrls: ['/logo.png', '/logo.png'] },
  { id: '4', title: '2025년 10월 13일 주보', date: '2025-10-13', thumbnailUrl: '/logo.png', pageUrls: ['/logo.png', '/logo.png'] },
];

const BulletinGallery: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [bulletins, setBulletins] = useState<Bulletin[]>([]);
  const [selectedBulletin, setSelectedBulletin] = useState<Bulletin | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBulletins = async () => {
      try {
        const backendUrl = process.env.REACT_APP_API_URL || 'https://ijbc-backend-1089361061119.us-west1.run.app';
        const response = await fetch(`${backendUrl}/api/bulletins`);
        const data = await response.json();

        if (data.success) {
          setBulletins(data.bulletins);
        } else {
          // Fallback to mock data on API failure
          setBulletins(mockBulletins);
          setError(data.error || 'Failed to fetch bulletins. Displaying mock data.');
        }
      } catch (err) {
        // Fallback to mock data on connection error
        setBulletins(mockBulletins);
        setError('Could not connect to the server. Displaying mock data.');
      }
      setIsLoading(false);
    };

    fetchBulletins();
  }, []);

  const filteredBulletins = bulletins.filter(bulletin =>
    bulletin.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewBulletin = (bulletin: Bulletin) => {
    setSelectedBulletin(bulletin);
  };

  const closeModal = () => {
    setSelectedBulletin(null);
  };

  const isPdf = (fileUrl?: string) => {
    return fileUrl?.toLowerCase().endsWith('.pdf');
  };

  const isImage = (fileUrl?: string) => {
    if (!fileUrl) return false;
    const lowerUrl = fileUrl.toLowerCase();
    return lowerUrl.endsWith('.jpg') || lowerUrl.endsWith('.jpeg') || lowerUrl.endsWith('.png') || lowerUrl.endsWith('.gif');
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const handleDelete = async (id: string) => {
    const apiKey = window.prompt('Please enter the Admin API Key to delete this bulletin:');
    if (!apiKey) return;

    if (!window.confirm('Are you sure you want to delete this bulletin? This action cannot be undone.')) return;

    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'https://ijbc-backend-1089361061119.us-west1.run.app';
      const response = await fetch(`${backendUrl}/api/bulletins/${id}`, {
        method: 'DELETE',
        headers: {
          'x-api-key': apiKey
        }
      });

      const data = await response.json();

      if (data.success) {
        alert('Bulletin deleted successfully.');
        setBulletins(prev => prev.filter(b => b.id !== id));
      } else {
        alert(`Failed to delete: ${data.error}`);
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('An error occurred while deleting the bulletin.');
    }
  };

  return (
    <GalleryContainer>
      <GalleryTitle>주보</GalleryTitle>

      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <SearchButton>🔍</SearchButton>
      </SearchContainer>

      {isLoading && <p style={{ textAlign: 'center' }}>Loading bulletins...</p>}
      {error && <p style={{ textAlign: 'center', color: 'red' }}>{error}</p>}

      <BulletinGrid>
        {filteredBulletins.map((bulletin) => (
          <BulletinCard key={bulletin.id} onClick={() => handleViewBulletin(bulletin)}>
            <BulletinImageContainer>
              {bulletin.thumbnailUrl ? (
                <img src={bulletin.thumbnailUrl} alt={bulletin.title} />
              ) : isPdf(bulletin.fileUrl) ? (
                <Document
                  file={bulletin.fileUrl}
                  loading={
                    <div style={{
                      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999'
                    }}>
                      Loading Preview...
                    </div>
                  }
                  error={
                    <div style={{
                      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#dc3545'
                    }}>
                      <span style={{ fontSize: '32px' }}>📄</span>
                      <span style={{ fontSize: '12px', marginTop: '5px' }}>PDF</span>
                    </div>
                  }
                >
                  <Page pageNumber={1} renderTextLayer={false} renderAnnotationLayer={false} width={300} />
                </Document>
              ) : isImage(bulletin.fileUrl) ? (
                <img src={bulletin.fileUrl} alt={bulletin.title} onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }} />
              ) : (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f8f9fa',
                  color: '#666'
                }}>
                  <span style={{ fontSize: '48px' }}>📁</span>
                </div>
              )}
            </BulletinImageContainer>
            <BulletinInfo>
              <BulletinDate>{formatDate(bulletin.date)}</BulletinDate>
              <ChurchName>
                <img src="/logo.png" alt="Logo" onError={(e) => (e.target as HTMLImageElement).style.display = 'none'} />
                얼바인중앙침례교회
              </ChurchName>
            </BulletinInfo>
            <DeleteButton
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(bulletin.id);
              }}
              title="Delete Bulletin"
            >
              🗑️
            </DeleteButton>
          </BulletinCard>
        ))}
      </BulletinGrid>

      <Modal isOpen={!!selectedBulletin} onClick={closeModal}>
        {selectedBulletin && (
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{selectedBulletin.title}</ModalTitle>
              <CloseButton onClick={closeModal}>×</CloseButton>
            </ModalHeader>

            <ModalBody>
              {isPdf(selectedBulletin.fileUrl) ? (
                <iframe
                  src={selectedBulletin.fileUrl}
                  title={selectedBulletin.title}
                  width="100%"
                  height="100%"
                  style={{ border: 'none', background: 'white' }}
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', alignItems: 'center' }}>
                  {selectedBulletin.pageUrls && selectedBulletin.pageUrls.length > 0 ? (
                    selectedBulletin.pageUrls.map((url, index) => (
                      <img
                        key={index}
                        src={url}
                        alt={`${selectedBulletin.title} - Page ${index + 1}`}
                        style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
                      />
                    ))
                  ) : (
                    <img
                      src={selectedBulletin.fileUrl || '/logo.png'}
                      alt={selectedBulletin.title}
                      style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                    />
                  )}
                </div>
              )}
            </ModalBody>

            <ModalFooter>
              <ActionButton href={selectedBulletin.fileUrl} download target="_blank" rel="noopener noreferrer">
                Download / Open
              </ActionButton>
            </ModalFooter>
          </ModalContent>
        )}
      </Modal>
    </GalleryContainer>
  );
};

export default BulletinGallery;
